import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axiosInstance";
import DataTable from "react-data-table-component";
import {
  Box,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  useTheme,
  Button,
  Paper,
  Divider,
} from "@mui/material";
import EditImproveQuestion from "./EditImproveQuestion";

export default function ListImproveQuestions({
  chatbotId,
  dataRefresh,
  setDataRefresh,
  permission,
}) {
  const theme = useTheme();
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null); // Added for success messages
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    document.title = "List Improve Questions | KlonIT Web Application";
    fetchDocuments();
  }, [dataRefresh]);

  const fetchDocuments = async () => {
    try {
      setLoading(true); // Ensure loading is true at the start
      const params = chatbotId ? { chatbot_id: chatbotId } : {};
      const response = await axiosInstance.get(`/get-improve-question`, {
        params,
      });
      const responseData =
        typeof response.data.data === "string"
          ? JSON.parse(response.data.data)
          : response.data.data;

      setDocuments(responseData);
      setFilteredDocuments(responseData);
      setError(null); // Clear previous errors
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Error fetching data: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);
    const filteredData = documents.filter(
      (d) =>
        d.questions.toLowerCase().includes(value) ||
        d.answers.toLowerCase().includes(value) ||
        d.question_improvement.toLowerCase().includes(value)
    );
    setFilteredDocuments(filteredData);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;

    try {
      const params = chatbotId ? { chatbot_id: chatbotId } : {};
      await axiosInstance.delete(`/delete-improve-question/${id}`, { params });
      setDocuments(documents.filter((doc) => doc.id !== id));
      setFilteredDocuments(filteredDocuments.filter((doc) => doc.id !== id));
      setSuccess("Data has been deleted successfully.");
      setTimeout(() => setSuccess(null), 3000); // Clear success message after 3 seconds
    } catch (error) {
      console.error("Error deleting data:", error);
      setError("Error deleting data: " + (error.response?.data?.message || error.message));
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleEditClick = (doc) => setSelectedItem(doc);
  const handleCloseModal = () => setSelectedItem(null);

  const columns = [
    { name: "Questions", selector: (row) => row.questions, sortable: true, maxWidth: '315px'  },
    { name: "Answers", selector: (row) => row.answers, sortable: true, maxWidth: '315px' },
    {
      name: "Improved Questions",
      selector: (row) => row.question_improvement,
      sortable: true,
      maxWidth: '315px'
    },
    {
      name: "Is Trained?",
      selector: (row) => (row.is_trained ? "Yes" : "No"),
      sortable: true,
    },
    {
      name: "Created",
      selector: (row) => new Date(row.created_date).toLocaleDateString(),
      sortable: true,
    },
    {
      name: "Action",
      cell: (row) => (
        <Box display="flex" gap={1}>
          {permission.canModify && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => handleEditClick(row)}
            >
              <i className="fa fa-pencil" />
            </Button>
          )}
          {permission.canDelete && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => handleDelete(row.id)}
            >
              <i className="fa fa-trash-o" />
            </Button>
          )}
        </Box>
      ),
      ignoreRowClick: true,
    },
  ];

  // Copied directly from ListRoles for consistency
  const customDataTableStyles = {
    table: {
      style: {
        backgroundColor: theme.palette.background.paper,
      },
    },
    headRow: {
      style: {
        backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9",
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${
          theme.palette.mode === "dark" ? "#334155" : "#cbd5e1"
        }`,
      },
    },
    headCells: {
      style: {
        fontWeight: 600,
        fontSize: "14px",
        color: theme.palette.text.primary,
      },
    },
    rows: {
      style: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor:
            theme.palette.mode === "dark" ? "#131b2b !important" : "#f8fafc",
        },
      },
    },
    pagination: {
      style: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderTop: `1px solid ${
          theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"
        }`,
      },
    },
    expanderRow: {
      style: {
        backgroundColor: theme.palette.mode === "dark" ? "#0f172a" : "#f9fafb",
      },
    },
  };

  const ExpandedComponent = ({ data }) => (
    <Box p={2} sx={{ backgroundColor: theme.palette.action.hover }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Questions:</strong> {data.questions}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Answers:</strong> {data.answers}
        </Typography>
        <Typography variant="body2">
            <strong>Improved Question:</strong> {data.question_improvement}
        </Typography>
    </Box>
  );


  return (
  <Paper sx={{ p: 3, mt: 10, mx: 'auto', maxWidth: '1200px', bgcolor: 'background.paper' }}>
      <Typography variant="h5" component="h1" sx={{ mb: 3}} color="textPrimary">
        Improve <strong>Questions</strong>
      </Typography>
      <Divider sx={{ mb: 3 }} />
    <Box>
      {selectedItem && (
        <EditImproveQuestion
          selectedItem={selectedItem}
          onSave={handleCloseModal} // Renamed for clarity
          onCancel={handleCloseModal}
          chatbotId={chatbotId}
          permission={permission}
        />
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" color="text.primary">
          Improve the questions for better clarity and effectiveness
        </Typography>
        <TextField
            size="small"
            variant="outlined"
            placeholder="Search..."
            value={searchTerm}
            onChange={handleSearch}
            sx={{ minWidth: '250px' }}
        />
      </Box>


      {loading ? (
        <Box display="flex" flexDirection="column" alignItems="center" py={4}>
          <CircularProgress size={30} />
          <Typography mt={2}>Loading questions...</Typography>
        </Box>
      ) : filteredDocuments.length === 0 ? (
        <Box textAlign="center" py={4}>
           <i className="fa fa-question-circle-o fa-3x text-muted" />
           <Typography mt={2} color="text.primary">
            No data available
           </Typography>
        </Box>
      ) : (
        <DataTable
          columns={columns}
          data={filteredDocuments}
          pagination
          responsive
          highlightOnHover
          pointerOnHover
          expandableRows
          expandableRowsComponent={ExpandedComponent}
          customStyles={customDataTableStyles}
          // Added theme prop and removed striped
          theme={theme.palette.mode === "dark" ? "dark" : "default"}
        />
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          <i className="fa fa-exclamation-triangle" /> {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mt: 3 }}>
          <i className="fa fa-check" /> {success}
        </Alert>
      )}
    </Box>
    </Paper>
  );
}